import React, { ReactElement, useEffect, useState } from 'react'
import { useSelector } from 'react-redux'
import { Alert, Box, Snackbar, Typography } from '@mui/material'
import { AxiosError } from 'axios'

import { apiZodios } from '~api/apiZodios'
import { EditIcon } from '~assets/icons'
import { Button, DeleteButton, LocationEntryModal } from '~components/common'
import Table, { TableHeader } from '~components/common/Table'
import { lightTheme } from '~createTheme'
import { RootState } from '~redux/rootReducer'
import { Address0, ContactId, HttpStatus, formatAddress0 } from '~types'

import AddressDisplay from './AddressDisplay'

type AddressSettingsTableProps = {
  contactId: ContactId
  onSave?: () => void
  inputAddresses?: Address0[]
}

const AddressSettingsTable = ({
  contactId,
  onSave,
  inputAddresses,
}: AddressSettingsTableProps): ReactElement => {
  const user = useSelector((state: RootState) => state?.auth.user)
  const [addresses, setAddresses] = useState<Address0[]>(inputAddresses || [])
  const [showAddressInput, setShowAddressInput] = useState(false)
  const [addressInUseAlert, setAddressInUseAlert] = useState(false)
  const [isEditingAddress, setIsEditingAddress] = useState(false)
  const [editingAddress, setEditingAddress] = useState<Address0>()

  const listAddresses = async (): Promise<void> => {
    if (!user) {
      return
    }
    try {
      const response = await apiZodios.contacts_listAddresses({
        user_id: user?.id,
        contact_ids: [contactId],
      })
      setAddresses(response.addresses as Address0[])
    } catch (_err) {}
  }

  const handleAddAddress = async (address: Address0): Promise<void> => {
    if (!user) {
      return
    }
    setAddresses((prev) => [...prev, address])
    setShowAddressInput(false)
    try {
      if (isEditingAddress) {
        handleRemoveAddress(editingAddress as Address0)
        setIsEditingAddress(false)
      }
      await apiZodios.contacts_addAddress({
        user_id: user.id,
        address_id: address.id,
        contact_id: contactId,
      })
      listAddresses()
    } catch (error: unknown) {
      if (error instanceof AxiosError) {
        if (error.response?.status === HttpStatus.CONFLICT) {
          setAddressInUseAlert(true)
        }
      }
      listAddresses()
    }
  }

  const handleRemoveAddress = async (address: Address0): Promise<void> => {
    if (!user) {
      return
    }
    setAddresses((prev) => prev.filter((a) => a.id !== address.id))
    try {
      await apiZodios.contacts_removeAddress({
        user_id: user?.id,
        address_id: address.id,
        contact_id: contactId,
      })
      listAddresses()
    } catch (_err) {}
  }

  const handleCancelAddAddress = (): void => {
    setShowAddressInput(false)
    setIsEditingAddress(false)
  }

  const handleEditAddress = async (address: Address0): Promise<void> => {
    setIsEditingAddress(true)
    setEditingAddress(address)
    setShowAddressInput(true)
  }

  const handleCloseAlert = (_event?: React.SyntheticEvent | Event, reason?: string): void => {
    if (reason === 'clickaway') {
      return
    }
    setAddressInUseAlert(false)
  }

  useEffect(() => {
    listAddresses()
  }, [contactId, user])

  const AddressRowContent: React.FC<{ row: Address0 }> = ({ row }) => {
    return (
      <Box display='flex' alignItems='center' justifyContent='space-between' mb={1}>
        <Typography variant='h4' sx={{ mr: 2, flexShrink: 0, overflow: 'clip' }}>
          <AddressDisplay address={row} location={row.location} removable={true} />
        </Typography>
        <Box className='flex-v-align'>
          <Button onClick={() => handleEditAddress(row)}>
            <Box mr={1}>
              <EditIcon color={lightTheme.palette.grey[600]} />
            </Box>
            <Typography variant='body2'>Edit</Typography>
          </Button>
          <DeleteButton item={row} onDelete={handleRemoveAddress} itemName={formatAddress0(row)} />
        </Box>
      </Box>
    )
  }

  const AddressTableHeader: TableHeader<Address0>[] = [
    {
      id: 'address',
      label: 'Address Information',
      render: (row) => <AddressRowContent row={row} />,
    },
  ]

  return (
    <Box display='flex' flexDirection='column' width='800px' padding={2}>
      <Table<Address0>
        headers={AddressTableHeader}
        data={addresses}
        loading={false}
        ignoreMaxHeight={true}
      />
      <Box display='flex' flexDirection='column' width='100%'>
        {showAddressInput ? (
          <LocationEntryModal
            onSave={(address: Address0) => handleAddAddress(address)}
            onClose={handleCancelAddAddress}
            organizationId={contactId}
          />
        ) : (
          <Box display='flex' justifyContent='flex-end'>
            <Button
              variant='outlined'
              color='secondary'
              sx={{ m: 2 }}
              onClick={() => setShowAddressInput(true)}
            >
              Add Address
            </Button>
            {onSave && (
              <Button
                variant='contained'
                color='secondary'
                style={{ margin: lightTheme.spacing(2) }}
                onClick={onSave}
              >
                Save
              </Button>
            )}
          </Box>
        )}
      </Box>
      <Snackbar open={addressInUseAlert} autoHideDuration={5000} onClose={handleCloseAlert}>
        <Alert onClose={handleCloseAlert} severity='warning' sx={{ width: '100%' }}>
          You are already associated with this address.
        </Alert>
      </Snackbar>
    </Box>
  )
}

export default AddressSettingsTable
